import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

interface PublicSidebarProps {
  open: boolean;
  onClose: () => void;
  onLogin: () => Promise<void>;
  loggingIn: boolean;
  loginError: string;
}

const PublicSidebar: React.FC<PublicSidebarProps> = ({ open, onClose, onLogin, loggingIn, loginError }) => {
  const { pathname } = useLocation();
  const isCarp = pathname.startsWith("/carpe");
  const [groups, setGroups] = useState({ trout: true, carp: true });
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const closeAction = useRef(onClose);
  closeAction.current = onClose;

  useEffect(() => {
    if (!open) return;
    setGroups({ trout: true, carp: true });
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeAction.current();
      }
      if (event.key !== "Tab") return;
      const elements = Array.from(panelRef.current?.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), [tabindex='0']") || [])
        .filter(element => element.getClientRects().length > 0);
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      if (opener?.isConnected) opener.focus();
    };
  }, [open, isCarp]);

  if (!open) return null;
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${isActive ? "bg-emerald-50 text-emerald-800" : "text-gray-700 hover:bg-gray-50 hover:text-emerald-700"}`;
  const disciplineGroup = (key: "trout" | "carp", label: string, prefix: string) => (
    <li>
      <button type="button" className="flex w-full items-center justify-between rounded-lg px-3 py-3 font-semibold text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        aria-expanded={groups[key]} aria-controls={`public-${key}-links`} onClick={() => setGroups(previous => ({ ...previous, [key]: !previous[key] }))}>
        {label}
        <svg aria-hidden="true" className={`h-4 w-4 transition-transform ${groups[key] ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      <ul id={`public-${key}-links`} hidden={!groups[key]} className="ml-5 border-l border-gray-200 pl-2 space-y-1">
        {[["Campionati", "campionati"], ["Gare", "gare"], ["Statistiche", "statistiche"]].map(([text, route]) =>
          <li key={route}><NavLink to={`${prefix}/${route}`} className={linkClass} onClick={onClose}>{text}</NavLink></li>)}
      </ul>
    </li>
  );

  return (
    <div className="fixed inset-0 z-50">
      <div className="public-menu-backdrop absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside ref={panelRef} id="public-navigation" role="dialog" aria-modal="true" aria-labelledby="public-menu-title"
        className="public-menu-panel absolute right-0 top-0 flex h-full w-80 max-w-[calc(100vw-2rem)] flex-col">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 id="public-menu-title" className="font-semibold text-gray-900">Menu</h2>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Chiudi menu" className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
            <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 18 18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <nav aria-label="Navigazione del sito" className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            {[["Home", "/"], ["Chi siamo", "/chi-siamo"], ["Dove siamo", "/dove-siamo"], ["Contatti", "/contatti"]].map(([text, route]) =>
              <li key={route}><NavLink end to={route} className={linkClass} onClick={onClose}>{text}</NavLink></li>)}
          </ul>
          <div className="mx-3 mb-2 mt-5 border-t border-gray-200 pt-4 text-xs font-semibold uppercase tracking-wider text-gray-500">Discipline di pesca</div>
          <ul className="space-y-2">
            {disciplineGroup("trout", "Trout Area", "")}
            {disciplineGroup("carp", "Carpe e Carassi", "/carpe")}
          </ul>
        </nav>
        <div className="shrink-0 border-t border-gray-200 p-4">
          {loginError && <p role="alert" className="mb-2 text-sm text-red-700">{loginError}</p>}
          <button type="button" onClick={onLogin} disabled={loggingIn} className="w-full rounded-lg bg-emerald-50 px-4 py-3 font-semibold text-emerald-800 hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50">
            {loggingIn ? "Accesso in corso…" : "Area riservata"}
          </button>
        </div>
      </aside>
    </div>
  );
};

export default PublicSidebar;
